import { BitkitCombobox, BitkitComboboxProps } from '@bitrise/bitkit-v2';
import { useEffect, useState } from 'react';

import useBranchSearch from './useBranchSearch';
import { validateBranchName } from './validateBranchName';

export interface BranchSelectorProps extends Omit<
  BitkitComboboxProps,
  'isLoading' | 'items' | 'onScrolledToBottom' | 'onSearchChange' | 'onSelect' | 'onValueChange' | 'value'
> {
  isQueryEnabled: boolean;
  isRequired?: boolean;
  onError?: () => void;
  onSelect: (selectedBranchName: string, isValid: boolean) => void;
  selectedBranch?: string;
  slug: string;
}

const BranchSelector = (props: BranchSelectorProps) => {
  const { isQueryEnabled, isRequired, onError, onSelect, selectedBranch, slug, ...rest } = props;

  const branchSearch = useBranchSearch(slug || '', { enabled: isQueryEnabled, retry: 1, selectedBranch });

  const [isInvalid, setIsInvalid] = useState(false);

  useEffect(() => {
    if (onError && branchSearch.isError) {
      onError();
    }
  }, [branchSearch.isError, onError]);

  const handleValueChange = (branchName: string | undefined) => {
    const newBranch = branchName ?? '';
    const isValid = validateBranchName(newBranch, isRequired);
    setIsInvalid(!isValid);
    onSelect(newBranch, isValid);
  };

  return (
    <BitkitCombobox
      allowCustomValue
      emptyLabel={branchSearch.hasNoBranches ? 'No branches to show' : 'No matching branches found'}
      emptyHelperText="Enter a branch name to use it."
      errorText={isInvalid ? 'Enter a valid branch name.' : undefined}
      {...branchSearch.comboboxProps}
      onValueChange={handleValueChange}
      optional={!isRequired}
      placeholder="Select or enter a branch name"
      value={selectedBranch ?? ''}
      {...rest}
    />
  );
};

export default BranchSelector;
